import {Timestamp} from "firebase/firestore";
import {cloneDeep} from "lodash";

export enum ReservationStatus {
  ACCEPTED,
  BOOKING_REQUESTED,
  BOOKING_REJECTED,
  CANCELED,
  CANCEL_REQUESTED,
  CANCEL_REJECTED,
}

export enum UserRole {
  ANONYM,
  NORMAL_ACCOUNT,
  ADMIN
}

export enum HouseName {
  Stockach = "Stockach",
  Biberwier = "Biberwier",
  Pitztal = "Pitztal"
}

export type Owner = {
  impressum: string;
  email: string;
  phone: string;
  name: string;
}

export type ReservationSettings = {
  allocationCheckerPublic: boolean;
  maxAllowedBookingDate: Timestamp;
  emailOnReservationUpdate: boolean;
  bookingRestrictionDays: number;
}

export type Settings = {
  reservation: ReservationSettings;
}

export type RoomReservation = {
  id?: string;
  userid?: string;
  roomName: string;
  fullHouse: boolean;
  houseName: string;
  from?: Timestamp;
  to?: Timestamp;
  status: ReservationStatus;
  lastUpdate?: Timestamp;
  createdDate?: Timestamp;
  userComment?: string;
  adminComment?: string;
  user?: DBUser;
  guestCount: number;
  booker?: "admin" | "user";
};

export type THouses = Map<string, House>

export type House = {
  id?: string;
  rooms: Map<string, Room>;
  capacity: number;
  houseName: string;
  description: string;
  fullHouseOption: boolean;
};

export type Room = {
  id?: string;
  roomName: string;
  capacity: number;
  description: string;
};

export const EMPTY_RESERVATION: RoomReservation = {
  roomName: "",
  houseName: "",
  fullHouse: false,
  guestCount: 0,
  status: ReservationStatus.BOOKING_REQUESTED,
};

export type DBUser = {
  userid: string;
  firstname: string;
  lastname: string;
  bookingCount: number;
  role?: UserRole;
};

export const fullHouseText = "Gesamtes Haus";

/**
 * Helper function to distinguish E-Mail text for a single room or the complete house.
 * @param roomName
 * @param lowerCase starts Gesamtes Haus either with lowercase or bigcase
 * @returns
 */
export function getRoomName(roomName:string, lowerCase = true): string {
  if (roomName === fullHouseText && lowerCase) {
    return fullHouseText.charAt(0).toLowerCase() + fullHouseText.slice(1);
  } else if (roomName === fullHouseText) {
    return fullHouseText;
  } else {
    return "Zimmer " + roomName;
  }
}

export const getAvailableRooms = (house: House): Room[] => {
  const res = cloneDeep(house.rooms);
  return Array.from(res.values());
};

/**
 * Get readable user name for user obejct.
 * @param user
 * @returns
 */
export function getDisplayName(user: DBUser) {
  return user.firstname + " " + user.lastname;
}

/**
 * Convert reservation enum to string.
 * @param status
 * @returns
 */
export function reservationStatusToStr(status: ReservationStatus | undefined) {
  switch (status) {
    case ReservationStatus.BOOKING_REQUESTED:
      return "Buchungsanfrage";
    case ReservationStatus.ACCEPTED:
      return "Buchung angenommen";
    case ReservationStatus.BOOKING_REJECTED:
      return "Buchung abgelehnt";
    case ReservationStatus.CANCELED:
      return "Buchung storniert";
    case ReservationStatus.CANCEL_REQUESTED:
      return "Stornierungsanfrage";
    case ReservationStatus.CANCEL_REJECTED:
      return "Stornierung abgelehnt";
    default:
      return "Unbekannter Status";
  }
}

/**
 * Get name for an enum.
 * @param role
 * @returns
 */
export function getRole(role: UserRole): string {
  switch (role) {
    case UserRole.ADMIN:
      return "Admin";
    default:
      return "Mitglied";
  }
}
